import { createContextLogger } from './logger';

const logger = createContextLogger('deleteUtils');

export interface DeleteNodeParams {
  nodeId: string;
  deleteNode: (id: string) => void;
  updateTabModified?: (pipelineId: string, isModified: boolean) => void;
  markStructuralChange?: (pipelineId: string) => void;
  pipelineId?: string;
}

export interface DeleteEdgeParams {
  edgeId: string;
  deleteEdge: (id: string) => void;
  updateTabModified?: (pipelineId: string, isModified: boolean) => void;
  pipelineId?: string;
}

export interface DeleteSelectedParams {
  selectedNodes: Array<{ id: string; data?: { name?: string; label?: string }; type?: string }>;
  selectedEdges?: Array<{ id: string; source?: string; target?: string }>;
  deleteNode: (id: string) => void;
  deleteEdge?: (id: string) => void;
  updateTabModified?: (pipelineId: string, isModified: boolean) => void;
  saveToHistory?: (description: string) => void;
  markStructuralChange?: (pipelineId: string) => void;
  pipelineId?: string;
}

/**
 * Delete a single node with proper logging and state management
 */
export const deleteNodeWithLogging = ({ 
  nodeId, 
  deleteNode, 
  updateTabModified,
  markStructuralChange,
  pipelineId 
}: DeleteNodeParams): void => {
  logger.debug('Deleting node:', { nodeId });
  
  // Perform the deletion
  deleteNode(nodeId);
  
  // Mark tab as modified if pipeline ID is provided
  if (updateTabModified && pipelineId) {
    updateTabModified(pipelineId, true);
  }
  
  // Mark structural change (deleting a node is a structural change)
  if (markStructuralChange && pipelineId) {
    markStructuralChange(pipelineId);
    logger.debug('Marked pipeline as having structural changes after node deletion');
  }
  
  logger.debug('Node deleted successfully:', { nodeId });
};

/**
 * Delete a single edge with proper logging and state management
 */
export const deleteEdgeWithLogging = ({ 
  edgeId, 
  deleteEdge, 
  updateTabModified, 
  pipelineId 
}: DeleteEdgeParams): void => {
  logger.debug('Deleting edge:', { edgeId });
  
  // Perform the deletion
  deleteEdge(edgeId);
  
  // Mark tab as modified if pipeline ID is provided
  if (updateTabModified && pipelineId) {
    updateTabModified(pipelineId, true);
  }
  
  logger.debug('Edge deleted successfully:', { edgeId });
};

/**
 * Delete multiple selected nodes and edges with proper logging
 */
export const deleteSelectedWithLogging = ({ 
  selectedNodes,
  selectedEdges = [],
  deleteNode,
  deleteEdge,
  updateTabModified,
  saveToHistory,
  markStructuralChange,
  pipelineId
}: DeleteSelectedParams): void => {
  logger.debug('Deleting selected items:', {
    nodeCount: selectedNodes.length,
    edgeCount: selectedEdges.length,
    nodes: selectedNodes.map(node => ({
      id: node.id,
      name: node.data?.name || node.data?.label,
      type: node.type
    })),
    edges: selectedEdges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target
    }))
  });

  // Delete nodes
  selectedNodes.forEach(node => {
    deleteNode(node.id);
  });

  // Delete edges if deleteEdge function is provided
  if (deleteEdge) {
    selectedEdges.forEach(edge => {
      deleteEdge(edge.id);
    });
  }

  // Mark tab as modified if any items were deleted
  if (updateTabModified && pipelineId && (selectedNodes.length > 0 || selectedEdges.length > 0)) {
    updateTabModified(pipelineId, true);
  }

  // Mark structural change if any items were deleted (deleting nodes/edges is a structural change)
  if (markStructuralChange && pipelineId && (selectedNodes.length > 0 || selectedEdges.length > 0)) {
    markStructuralChange(pipelineId);
    logger.debug('Marked pipeline as having structural changes after deletion');
  }

  // Save to history if any items were deleted
  if (saveToHistory && (selectedNodes.length > 0 || selectedEdges.length > 0)) {
    const descriptions = [];
    if (selectedNodes.length > 0) {
      descriptions.push(`Delete ${selectedNodes.length} node${selectedNodes.length > 1 ? 's' : ''}`);
    }
    if (selectedEdges.length > 0) {
      descriptions.push(`Delete ${selectedEdges.length} edge${selectedEdges.length > 1 ? 's' : ''}`);
    }
    saveToHistory(descriptions.join(', '));
  }

  logger.debug('Selected items deleted successfully:', {
    deletedNodes: selectedNodes.length,
    deletedEdges: selectedEdges.length
  });
}; 